import { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth, db } from '../firebase/client';
import { doc, setDoc, getDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('user');

  async function signup(email, password, name) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, { displayName: name });
      
      // Create user document in Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        name,
        email,
        role: 'user',
        createdAt: new Date().toISOString()
      });

      // Find and associate any guest bookings with this email
      const bookingsRef = collection(db, 'bookings');
      const guestBookingsQuery = query(
        bookingsRef,
        where('userEmail', '==', email),
        where('isGuestBooking', '==', true)
      );

      const guestBookingsSnapshot = await getDocs(guestBookingsQuery);
      
      // Update each guest booking to be associated with the new user
      const updatePromises = guestBookingsSnapshot.docs.map(async (bookingDoc) => {
        const bookingData = bookingDoc.data();
        const bookingRef = doc(db, 'bookings', bookingDoc.id);
        
        // Update the main booking document
        await updateDoc(bookingRef, {
          userId: userCredential.user.uid,
          isGuestBooking: false
        });

        // Create a copy in the user's bookings collection
        await setDoc(doc(db, 'users', userCredential.user.uid, 'bookings', bookingDoc.id), {
          ...bookingData,
          userId: userCredential.user.uid,
          isGuestBooking: false
        });
      });

      await Promise.all(updatePromises);
      
      return userCredential.user;
    } catch (error) {
      throw error;
    }
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    return signOut(auth);
  }

  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  async function getUserRole(uid) {
    try {
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return docSnap.data().role;
      }
      return 'user';
    } catch (error) {
      console.error("Error getting user role:", error);
      return 'user';
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        const role = await getUserRole(user.uid);
        setUserRole(role);
      } else {
        setUserRole('user');
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    signup,
    login,
    logout,
    resetPassword,
    isAdmin: userRole === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 